import { z } from 'zod';
import { GeoSchema, I18nStringSchema, SlugSchema, TimestampSchema } from './primitives.js';
import { HoursSchema } from './branding.js';

export const OutletStatusSchema = z.enum(['provisioned', 'active', 'soft_closed', 'archived']);
export type OutletStatus = z.infer<typeof OutletStatusSchema>;

/**
 * Sprint 106 — VehicleType joined the tenant library at
 * `tenants/{tid}/vehicleTypes/{id}`. `excludedOutletIds` follows the
 * same smart-default pattern as the other library kinds (empty =
 * available everywhere). See `docs/architecture/tenant-library.md`.
 */
export const VehicleTypeSchema = z.object({
  id: z.string(),
  name: I18nStringSchema,
  icon: z.string().optional().describe('icon name; rendered by the UI icon system'),
  active: z.boolean().default(true),
  excludedOutletIds: z
    .array(z.string())
    .default([])
    .describe('Outlets where this vehicle type is HIDDEN. Empty (default) = available everywhere.'),
});
export type VehicleType = z.infer<typeof VehicleTypeSchema>;

export const BaySchema = z.object({
  id: z.string(),
  name: I18nStringSchema,
  active: z.boolean().default(true),
});
export type Bay = z.infer<typeof BaySchema>;

export const OutletProfileSchema = z.object({
  name: z.string().min(1).max(80),
  slug: SlugSchema,
  status: OutletStatusSchema,
  address: z.string(),
  geo: GeoSchema,
  hours: HoursSchema,
  photoUrls: z.array(z.string().url()).default([]),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type OutletProfile = z.infer<typeof OutletProfileSchema>;

export const CatalogSettingsSchema = z.object({
  bookingMode: z.enum(['package', 'alaCarte', 'both']).default('both'),
  pricingVersion: z.number().int().nonnegative().default(0),
  allowSubStages: z
    .boolean()
    .default(false)
    .describe('Internal sub-stages under "In Washing Process"'),
});
export type CatalogSettings = z.infer<typeof CatalogSettingsSchema>;

export const QueueSettingsSchema = z.object({
  washerCanReorder: z.boolean().default(false),
  defaultSlotMinutes: z.number().int().positive().default(15),
  capacityPerBay: z.number().int().positive().default(1),
});
export type QueueSettings = z.infer<typeof QueueSettingsSchema>;
